import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';

import errorLogger, { BUGSNAG_STATUSES } from '@picsart/growth-rc/services/errorLogger';
import { getContext } from 'configs/context';

import DEFAULT_SETTINGS_JSON from 'utils/remoteSettings.json';
import type { RemoteSettings } from 'types';

import { REMOTE_SETTINGS_TYPES } from 'configs/constants';

// JSON imports widen string literals (e.g. `type: "webhook"` → `string`), which breaks
// discriminated unions like IPreset. Cast through `unknown` so the strict shape applies.
const DEFAULT_SETTINGS = DEFAULT_SETTINGS_JSON as unknown as RemoteSettings;

const RemoteSettingsContext = createContext<RemoteSettings>(DEFAULT_SETTINGS);

export const useRemoteSettings = () => useContext<RemoteSettings>(RemoteSettingsContext);

export const RemoteSettingsProvider = ({ children }: { children: ReactNode }) => {
  const context = getContext();
  const [settings, setSettings] = useState<RemoteSettings>(DEFAULT_SETTINGS);

  const isMounted = useRef(true);

  const fetchSettings = useCallback(async () => {
    const settingsMap = Object.entries(REMOTE_SETTINGS_TYPES);

    await Promise.all(
      settingsMap.map(async ([key, value]) => {
        try {
          const remoteSettings = await context.handlers.getRemoteSettings(key, value);
          const isValidObject = remoteSettings !== null && typeof remoteSettings === 'object' && !Array.isArray(remoteSettings) && Object.keys(remoteSettings).length > 0;
          if (isValidObject || typeof remoteSettings === 'string' || typeof remoteSettings === 'boolean') {
            setSettings(prevState => ({
              ...prevState,
              [REMOTE_SETTINGS_TYPES[key as keyof typeof REMOTE_SETTINGS_TYPES]]: remoteSettings,
            }));
          } else {
            console.warn('Remote settings are empty or invalid, using default settings.');
            setSettings(
              prevState =>
                ({
                  ...prevState,
                  [REMOTE_SETTINGS_TYPES[key as keyof typeof REMOTE_SETTINGS_TYPES]]: remoteSettings,
                } as any),
            );
          }
        } catch (err) {
          console.error('Error fetching remote settings:', err);
          errorLogger.trigger(err as Error, BUGSNAG_STATUSES.ERROR);
          setSettings(DEFAULT_SETTINGS);
        }
      }),
    );
  }, [context.handlers]);

  useEffect(() => {
    isMounted.current = true;
    fetchSettings();

    return () => {
      // Cleanup: Set ref false to avoid setting state after unmount
      isMounted.current = false;
    };
  }, [context, fetchSettings]);

  return <RemoteSettingsContext.Provider value={settings}>{children}</RemoteSettingsContext.Provider>;
};
